"use client";

import { useDeferredValue, useEffect, useMemo, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  checkSpelling,
  createDictionary,
  groupFindings,
  parseIgnoreList,
  type Region,
  replaceSpans,
  suggest,
  type UniqueFinding,
} from "@/lib/tool-kit/style-checkers/spell-check";
import { loadWordList } from "@/lib/tool-kit/vocabulary/anagram";

/** English strings. A translated content file overrides these through `ui`. */
export const SPELL_CHECKER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a paragraph, an essay or a whole paper…",
  privacy:
    "Your text is checked in your browser and never uploaded. The only thing fetched is the word list, once per visit.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "Teh results were definately significant, but we recieve the data in a seperate file. The favourite colour of the Zotero team is grey. Participants' self-reports were well-validated, and the dataset is on GitHub.",
  regionLabel: "Spelling variety",
  regionBoth: "Accept both US and UK",
  regionUs: "American English",
  regionUk: "British English",
  ignoreCapitalised: "Skip capitalised words inside a sentence (usually names)",
  ignoreAcronyms: "Skip ALL-CAPS acronyms such as APA or NASA",
  ignoreLabel: "Words to ignore",
  ignoreHint:
    "Separate words with commas, spaces or new lines. Case does not matter. Click Ignore on a result to add a word here.",
  ignorePlaceholder: "Zotero, Mendeley",
  loading: "Loading the word list (once per visit)…",
  loadError:
    "Could not load the word list. Check your connection and try again.",
  retry: "Try again",
  enterPrompt: "Paste or type some text to check its spelling.",
  results: "Results",
  checked: "Words checked",
  misspelled: "Unknown words",
  occurrences: "Occurrences",
  regional: "Other-variety spellings",
  readingNone:
    "Every word checked is in the dictionary. That does not mean the text is error-free: a correctly spelled wrong word (their for there) cannot be found this way.",
  readingSome:
    "Words below are not in the dictionary. Some will be typos; others are names, jargon or new words the list has never seen. Ignore those.",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with unknown words highlighted",
  legendUnknown: "Not in the dictionary",
  legendRegional: "Other variety",
  unknownHeading: "Unknown words",
  suggestionsLabel: "Replace with:",
  noSuggestions: "No close match in the dictionary.",
  ignore: "Ignore",
  times: "one: time|other: times",
  moreSuggestions: "Show {count} more",
  regionalHeading: "Spellings from the other variety",
  regionalNote:
    "These are correct spellings, flagged only because you chose {variety}.",
  varietyUs: "American English",
  varietyUk: "British English",
  replaceAll: "Change to {word}",
  sourceNote:
    "Words come from ENABLE, a public-domain list of about 168,000 words used in word games, plus a short list of contractions, abbreviations, modern vocabulary and proper nouns added for this tool. ENABLE accepts both American and British spellings (colour and color), so the variety option is a separate check. Accented words are matched on their unaccented form.",
  limits:
    "A spell checker finds strings that are not words. It cannot catch a real word used in place of another, a missing word or a grammar error, and it does not know every name or technical term.",
} as const;

type Strings = typeof SPELL_CHECKER_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;
type ListState = "idle" | "loading" | "ready" | "error";

const TONES = { misspelling: "rose", regional: "sky" } as const;
const PAGE = 20;

function Occurrences({
  count,
  strings,
  fmt,
  locale,
}: {
  count: number;
  strings: Strings;
  fmt: Fmt;
  locale: string;
}) {
  return (
    <span className={HINT}>
      {fmt(count)}
      {tPlural(strings.times, locale, count)}
    </span>
  );
}

function UnknownCard({
  group,
  suggestions,
  strings,
  fmt,
  locale,
  onReplace,
  onIgnore,
}: {
  group: UniqueFinding;
  suggestions: string[];
  strings: Strings;
  fmt: Fmt;
  locale: string;
  onReplace: (group: UniqueFinding, replacement: string) => void;
  onIgnore: (key: string) => void;
}) {
  return (
    <li className={`${CARD} space-y-3`}>
      <div className="flex flex-wrap items-center gap-3">
        <code className="rounded bg-muted px-2 py-1 font-medium text-foreground text-sm">
          {group.word}
        </code>
        <Occurrences
          count={group.count}
          fmt={fmt}
          locale={locale}
          strings={strings}
        />
        <button
          className={`${BUTTON_SMALL} ms-auto`}
          onClick={() => onIgnore(group.key)}
          type="button"
        >
          {strings.ignore}
        </button>
      </div>
      {suggestions.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className={HINT}>{strings.suggestionsLabel}</span>
          {suggestions.map((word) => (
            <button
              className={BUTTON_SMALL}
              key={word}
              onClick={() => onReplace(group, word.toLowerCase())}
              type="button"
            >
              {word}
            </button>
          ))}
        </div>
      ) : (
        <p className={HINT}>{strings.noSuggestions}</p>
      )}
    </li>
  );
}

function RegionalCard({
  group,
  strings,
  fmt,
  locale,
  onReplace,
}: {
  group: UniqueFinding;
  strings: Strings;
  fmt: Fmt;
  locale: string;
  onReplace: (group: UniqueFinding, replacement: string) => void;
}) {
  const replacement = group.replacement ?? "";
  return (
    <li className={`${CARD} flex flex-wrap items-center gap-3`}>
      <code className="rounded bg-muted px-2 py-1 text-foreground text-sm">
        {group.word}
      </code>
      <span aria-hidden="true" className="text-muted-foreground">
        →
      </span>
      <code className="rounded bg-muted px-2 py-1 text-foreground text-sm">
        {replacement}
      </code>
      <Occurrences
        count={group.count}
        fmt={fmt}
        locale={locale}
        strings={strings}
      />
      <button
        className={`${BUTTON_SMALL} ms-auto`}
        onClick={() => onReplace(group, replacement.toLowerCase())}
        type="button"
      >
        {t(strings.replaceAll, { word: replacement })}
      </button>
    </li>
  );
}

export default function SpellCheckerWidget() {
  const strings = useToolUi(SPELL_CHECKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [region, setRegion] = useState<Region>("both");
  const [ignoreCapitalised, setIgnoreCapitalised] = useState(true);
  const [ignoreAcronyms, setIgnoreAcronyms] = useState(true);
  const [ignoreRaw, setIgnoreRaw] = useState("");
  const [words, setWords] = useState<string[] | null>(null);
  const [listState, setListState] = useState<ListState>("idle");
  const [shown, setShown] = useState(PAGE);
  const deferred = useDeferredValue(text);
  const hasText = deferred.trim().length > 0;

  useEffect(() => {
    if (!hasText || listState !== "idle") {
      return;
    }
    setListState("loading");
    loadWordList()
      .then((list) => {
        setWords(list);
        setListState("ready");
      })
      .catch(() => setListState("error"));
  }, [hasText, listState]);

  const dictionary = useMemo(
    () => (words ? createDictionary(words) : null),
    [words]
  );
  const ignore = useMemo(() => parseIgnoreList(ignoreRaw), [ignoreRaw]);
  const report = useMemo(
    () =>
      dictionary
        ? checkSpelling(deferred, dictionary, {
            ignoreCapitalised,
            ignoreAcronyms,
            region,
            ignore,
          })
        : null,
    [deferred, dictionary, ignoreCapitalised, ignoreAcronyms, region, ignore]
  );
  const groups = useMemo(
    () => (report ? groupFindings(report.findings) : []),
    [report]
  );
  const unknown = useMemo(
    () => groups.filter((group) => group.kind === "misspelling"),
    [groups]
  );
  const otherVariety = useMemo(
    () => groups.filter((group) => group.kind === "regional"),
    [groups]
  );
  const visible = useMemo(() => unknown.slice(0, shown), [unknown, shown]);
  const suggestions = useMemo(
    () =>
      dictionary
        ? visible.map((group) => suggest(group.word, dictionary, 5))
        : [],
    [dictionary, visible]
  );

  function replaceAll(group: UniqueFinding, replacement: string) {
    if (text === deferred) {
      setText(replaceSpans(text, group.spans, replacement));
    }
  }

  function addIgnore(key: string) {
    setIgnoreRaw((current) => (current.trim() ? `${current}, ${key}` : key));
  }

  const highlights: TextMatch[] = (report?.findings ?? []).map((finding) => ({
    start: finding.start,
    end: finding.end,
    category: finding.kind,
    title: finding.replacement ?? undefined,
  }));
  const varietyName = region === "us" ? strings.varietyUs : strings.varietyUk;

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.privacy}
        id="sc-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.exampleText)}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <PillGroup
        label={strings.regionLabel}
        name="sc-region"
        onChange={setRegion}
        options={[
          { value: "both", label: strings.regionBoth },
          { value: "us", label: strings.regionUs },
          { value: "uk", label: strings.regionUk },
        ]}
        value={region}
      />
      <div className="space-y-2">
        <Checkbox
          checked={ignoreCapitalised}
          label={strings.ignoreCapitalised}
          onChange={setIgnoreCapitalised}
        />
        <Checkbox
          checked={ignoreAcronyms}
          label={strings.ignoreAcronyms}
          onChange={setIgnoreAcronyms}
        />
      </div>
      <TextAreaField
        hint={strings.ignoreHint}
        id="sc-ignore"
        label={strings.ignoreLabel}
        onChange={setIgnoreRaw}
        placeholder={strings.ignorePlaceholder}
        prose
        rows={2}
        value={ignoreRaw}
      />

      {hasText && listState === "loading" ? (
        <output className="block text-muted-foreground text-sm">
          {strings.loading}
        </output>
      ) : null}
      {hasText && listState === "error" ? (
        <div className="space-y-3">
          <ErrorNotice>{strings.loadError}</ErrorNotice>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setListState("idle")}
            type="button"
          >
            {strings.retry}
          </button>
        </div>
      ) : null}
      {hasText ? null : <Notice>{strings.enterPrompt}</Notice>}

      {hasText && report ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.misspelled}
                value={fmt(unknown.length)}
              />
              <StatCard
                label={strings.occurrences}
                value={fmt(
                  unknown.reduce((sum, group) => sum + group.count, 0)
                )}
              />
              <StatCard
                label={strings.regional}
                value={fmt(otherVariety.length)}
              />
              <StatCard label={strings.checked} value={fmt(report.checked)} />
            </StatGrid>
            <Notice>
              {unknown.length === 0 ? strings.readingNone : strings.readingSome}
            </Notice>
          </div>

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <p className="flex flex-wrap gap-3 text-sm">
              <span className={HIGHLIGHT_TONES.rose}>
                {strings.legendUnknown}
              </span>
              {region === "both" ? null : (
                <span className={HIGHLIGHT_TONES.sky}>
                  {strings.legendRegional}
                </span>
              )}
            </p>
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={TONES}
            />
          </div>

          {unknown.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.unknownHeading}</h3>
              <ul className="space-y-3">
                {visible.map((group, index) => (
                  <UnknownCard
                    fmt={fmt}
                    group={group}
                    key={group.key}
                    locale={locale}
                    onIgnore={addIgnore}
                    onReplace={replaceAll}
                    strings={strings}
                    suggestions={suggestions[index] ?? []}
                  />
                ))}
              </ul>
              {unknown.length > shown ? (
                <button
                  className={BUTTON_SECONDARY}
                  onClick={() => setShown(shown + PAGE)}
                  type="button"
                >
                  {t(strings.moreSuggestions, {
                    count: fmt(Math.min(PAGE, unknown.length - shown)),
                  })}
                </button>
              ) : null}
            </div>
          ) : null}

          {otherVariety.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.regionalHeading}</h3>
              <p className={HINT}>
                {t(strings.regionalNote, { variety: varietyName })}
              </p>
              <ul className="space-y-3">
                {otherVariety.map((group) => (
                  <RegionalCard
                    fmt={fmt}
                    group={group}
                    key={group.key}
                    locale={locale}
                    onReplace={replaceAll}
                    strings={strings}
                  />
                ))}
              </ul>
            </div>
          ) : null}
          <p className={HINT}>{strings.sourceNote}</p>
          <p className={HINT}>{strings.limits}</p>
        </section>
      ) : null}
    </div>
  );
}
